import { Fragment, useEffect, useState } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import { shallowEqual, useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'

import ActionIcon from '@components/UI/ActionIcon'
import Drawer from '@components/UI/Drawer'
import LinkifiedString from '@components/UI/LinkifiedString'
import MediaSlider from '@components/UI/MediaSlider'
import { ConnectedTrackedRoute } from '@components/UI/TrackedRoute'
import { WHAT_IS_A_CONTENT_CAMPAIGN } from '@constants'
import { useCampaignContext } from '@context/Campaign'
import { EventTrackingNames, SupportedSubmissionTypes } from '@enums'
import { withCampaign } from '@hocs'
import { IApplicationState } from '@store'
import { selectBrand } from '@store/brands'
import {
  ICampaign,
  selectCampaignHouseRules,
  selectEditLink,
  selectIsOfferBrief,
  selectIsUnlockedCampaign,
  selectShowPaidPartnershipHandle as selectCampaignShowPaidPartnershipHandle,
} from '@store/campaigns'
import {
  IDraft,
  selectShowPaidPartnershipHandle as selectDraftShowPaidPartnershipHandle,
} from '@store/drafts'
import Theme from '@theme'
import { Container, Link as TribeLink, Text } from '@tribegroup'
import { generateEditLink } from '@utils'
import { isSocialCampaign } from '@utils/campaign'
import { BlockText } from './Block/Block.styled'
import CampaignPreviewTags from './Tags/Tags'
import CampaignPreviewTitle from './Title/Title'
import CampaignPreviewBlock from './Block'
import CampaignPreviewDeliverables from './Deliverables'
import CampaignPreviewDosAndDonts from './DosAndDonts'
import CampaignPreviewHeroImage from './HeroImage'
import CampaignPreviewHouseRules from './HouseRules'
import CampaignPreviewInfluencerRequirements from './InfluencerRequirements'
import CampaignPreviewObjective from './Objective'
import { CampaignPreviewPitching } from './Pitching'
import CampaignPreviewPreferredAudience from './PreferredAudience'
import { EmptyImage16x9 } from './Preview.styled'
import { CampaignPreviewSubmissionOptions } from './SubmissionOptions'

/**
 * Returns true if the campaign/draft is social, using the same predicate as
 * `selectDraftIsSocial` in the builder: either `submission_type` is already
 * set to SocialSubmission (new draft before first BE round-trip) OR
 * `supported_submission_types` contains 'social_submission' (saved campaign).
 * This is intentionally local so existing `isSocialCampaign` callers are unaffected.
 */
const isSocialForDeliverables = (campaign: ICampaign | IDraft | undefined): boolean =>
  Boolean(
    campaign &&
      ((campaign as IDraft).submission_type === SupportedSubmissionTypes.SocialSubmission ||
        isSocialCampaign(campaign)),
  )

interface IPreviewProps {
  draft?: IDraft
  open?: boolean
  onClose?: VoidFunction
}

const PREVIEW_BASE_PATH = '/preview'
const EMPTY_HOUSE_RULES: ReadonlyArray<string> = []

const CampaignPreview = ({ draft, open, onClose }: IPreviewProps) => {
  const intl = useIntl()
  const [isOpen, setIsOpen] = useState(false)
  const history = useHistory()

  const { campaign: campaignFromContext } = useCampaignContext()
  const isDraft = Boolean(draft)
  const campaign = isDraft ? (draft as any) : campaignFromContext

  const {
    hasEditPermission,
    campaignHouseRules,
    brand,
    hasBrandPartnershipHandle,
    isUnlockedCampaign,
    isOfferBrief,
  } = useSelector(
    (state: IApplicationState) => ({
      hasEditPermission: isDraft ? false : Boolean(selectEditLink(state, campaign?.id)),
      campaignHouseRules: isDraft
        ? EMPTY_HOUSE_RULES
        : selectCampaignHouseRules(state, campaign?.id),
      brand: selectBrand(state, campaign?.brand_id as number),
      hasBrandPartnershipHandle: isDraft
        ? selectDraftShowPaidPartnershipHandle(state)
        : selectCampaignShowPaidPartnershipHandle(state, campaign?.id),
      isUnlockedCampaign: selectIsUnlockedCampaign(state, campaign?.id),
      isOfferBrief: selectIsOfferBrief(state, campaign?.id),
    }),
    shallowEqual,
  )

  useEffect(() => {
    setIsOpen(history.location.pathname.includes(PREVIEW_BASE_PATH))
  }, [history.location])

  useEffect(() => {
    setIsOpen(Boolean(open))
  }, [open])

  const handleClose = () => {
    if (onClose) {
      return onClose()
    }

    const { pathname, search } = history.location
    history.push(`${pathname.replace(PREVIEW_BASE_PATH, '')}${search}`)
  }

  return (
    <Drawer
      width={20.75}
      title={intl.formatMessage({ id: 'inbox.preview.header.campaignPreview' })}
      open={isOpen}
      headerRightIcon={
        <ActionIcon
          size={1.5}
          glyph="close-x"
          color={Theme.grey900Color}
          onClick={handleClose}
          elementName="drawer-close-button"
        />
      }
      headerLeftIcon={
        hasEditPermission ? (
          <ConnectedTrackedRoute
            campaignId={campaign.id}
            eventName={EventTrackingNames.CampaignBuilderEditCampaign}
            additionalProperties={{ current_view: 'campaign_preview' }}
          >
            <Link to={generateEditLink(campaign)}>
              <ActionIcon
                size={1.5}
                glyph="edit"
                color={Theme.grey900Color}
                tooltip={intl.formatMessage({ id: 'inbox.titlebar.action.editCampaign' })}
                elementName="drawer-edit-button"
              />
            </Link>
          </ConnectedTrackedRoute>
        ) : null
      }
      wrapperCallback={handleClose}
    >
      <CampaignPreviewHeroImage
        logo={brand ? brand.logo_url : (campaign.brand_logo as string)}
        heroImage={campaign.hero_16x9_original_url}
      />
      {!isOfferBrief && (
        <CampaignPreviewTitle
          brandName={brand ? brand.name : (campaign.brand_name as string)}
          productName={campaign.product_name}
          subTitle={campaign.call_to_action}
        />
      )}
      {isSocialCampaign(campaign) && !campaign.is_brand_fans && !isOfferBrief && (
        <Fragment>
          <CampaignPreviewInfluencerRequirements campaign={campaign} />
          <CampaignPreviewPreferredAudience campaign={campaign} />
        </Fragment>
      )}
      <CampaignPreviewBlock
        title={<FormattedMessage id="campaign.preview.aboutUs.header" />}
        empty={!campaign.product_text}
        emptyLines={[16.875, 16.25, 15, 10]}
      >
        <BlockText small color={Theme.grey800Color}>
          <LinkifiedString value={campaign.product_text} color={Theme.primaryColor} />
        </BlockText>
      </CampaignPreviewBlock>
      <CampaignPreviewBlock title={<FormattedMessage id="campaign.preview.moodBoard.header" />} />
      {campaign.moodboard_images && campaign.moodboard_images.length > 0 ? (
        <MediaSlider mediaList={campaign.moodboard_images} />
      ) : (
        <EmptyImage16x9 withBottomSpace />
      )}
      <CampaignPreviewObjective text={campaign.objective} />
      {isSocialForDeliverables(campaign) && !isOfferBrief && (
        <CampaignPreviewDeliverables text={campaign.deliverables} />
      )}
      {!isUnlockedCampaign && !isOfferBrief && (
        <CampaignPreviewPitching
          pitchingEnabled={campaign.pitching_enabled}
          isSocial={isSocialCampaign(campaign)}
        />
      )}
      {!isSocialCampaign(campaign) && !isUnlockedCampaign && !isOfferBrief && (
        <CampaignPreviewBlock
          title={<FormattedMessage id="campaign.preview.contentCampaign.header" />}
          emptyLines={[15]}
        >
          <BlockText small color={Theme.grey800Color}>
            <FormattedMessage id="campaign.preview.contentCampaign.text" />
          </BlockText>
          <Container topOuterSpacing={0.5}>
            <TribeLink href={WHAT_IS_A_CONTENT_CAMPAIGN} target="_blank">
              <Text color={Theme.primaryColor} customFontSize={0.8125}>
                <FormattedMessage id="campaign.preview.contentCampaign.learnMore" />
              </Text>
            </TribeLink>
          </Container>
        </CampaignPreviewBlock>
      )}

      {isSocialCampaign(campaign) && !isOfferBrief && (
        <CampaignPreviewSubmissionOptions campaign={campaign} />
      )}

      {!isOfferBrief && <CampaignPreviewTags />}

      <CampaignPreviewDosAndDonts dos={campaign.dos!} donts={isOfferBrief ? [] : campaign.donts!} />
      <CampaignPreviewBlock
        title={<FormattedMessage id="campaign.preview.howToGetProduct.header" />}
        empty={!campaign.product_where_to_find}
        emptyLines={[12.5, 15]}
      >
        <BlockText small color={Theme.grey800Color}>
          {campaign.product_where_to_find}
        </BlockText>
      </CampaignPreviewBlock>
      {!isOfferBrief && (
        <Fragment>
          {!isUnlockedCampaign && (
            <CampaignPreviewBlock
              title={<FormattedMessage id="campaign.preview.campaignCurrency.header" />}
              empty={!campaign.budget_total_currency}
              emptyLines={[15]}
            >
              <BlockText small color={Theme.grey800Color}>
                <FormattedMessage
                  id="campaign.preview.campaignCurrency.text"
                  values={{ currencyCode: campaign.budget_total_currency }}
                />
              </BlockText>
            </CampaignPreviewBlock>
          )}
          <CampaignPreviewHouseRules rules={campaignHouseRules} />
        </Fragment>
      )}
    </Drawer>
  )
}

export const CampaignDraftPreview = CampaignPreview

export default withCampaign(CampaignPreview)
